import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminRules() {
  const [rules, setRules] = useState(null);
  const [services, setServices] = useState([]);
  const [newSvc, setNewSvc] = useState({ key: "", points: 100 });

  const load = async () => {
    const { data } = await api.get("/admin/rules");
    setRules(data);
    setServices(Object.entries(data.service_points || {}).map(([k, v]) => ({ key: k, points: v })));
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setRules({ ...rules, [k]: Number(v) });

  const save = async () => {
    const service_points = Object.fromEntries(services.map((s) => [s.key, Number(s.points)]));
    try {
      await api.put("/admin/rules", { ...rules, service_points });
      toast.success("Rules saved");
      await load();
    } catch { toast.error("Failed to save"); }
  };

  const addService = () => {
    if (!newSvc.key.trim()) return;
    setServices([...services, { key: newSvc.key.trim(), points: Number(newSvc.points) }]);
    setNewSvc({ key: "", points: 100 });
  };

  if (!rules) return <AdminShell title="Loyalty rules"><div /></AdminShell>;

  return (
    <AdminShell title="Loyalty rules">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-mr rounded-2xl p-6">
          <h2 className="font-display text-xl font-semibold text-mr-primary mb-4">Earning rules</h2>
          <div className="space-y-4">
            <div><Label>Points per AED spent</Label><Input data-testid="rules-aed" type="number" step="0.1" value={rules.points_per_aed} onChange={(e) => update("points_per_aed", e.target.value)} className="mt-2" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Welcome bonus</Label><Input data-testid="rules-welcome" type="number" value={rules.welcome_bonus} onChange={(e) => update("welcome_bonus", e.target.value)} className="mt-2" /></div>
              <div><Label>Birthday bonus</Label><Input data-testid="rules-birthday" type="number" value={rules.birthday_bonus} onChange={(e) => update("birthday_bonus", e.target.value)} className="mt-2" /></div>
              <div><Label>Anniversary bonus</Label><Input data-testid="rules-anniv" type="number" value={rules.anniversary_bonus} onChange={(e) => update("anniversary_bonus", e.target.value)} className="mt-2" /></div>
              <div><Label>Referral bonus</Label><Input data-testid="rules-referral" type="number" value={rules.referral_bonus} onChange={(e) => update("referral_bonus", e.target.value)} className="mt-2" /></div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-mr rounded-2xl p-6">
          <h2 className="font-display text-xl font-semibold text-mr-primary mb-2">Points per service</h2>
          <p className="text-mr-secondary text-sm mb-4">Set how many points patients earn for each service type.</p>
          <div className="space-y-2">
            {services.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <Input data-testid={`svc-key-${i}`} value={s.key} onChange={(e) => { const c = [...services]; c[i].key = e.target.value; setServices(c); }} className="flex-1" />
                <Input data-testid={`svc-points-${i}`} type="number" value={s.points} onChange={(e) => { const c = [...services]; c[i].points = e.target.value; setServices(c); }} className="w-24" />
                <Button data-testid={`svc-del-${i}`} variant="ghost" size="icon" onClick={() => setServices(services.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-mr-border">
              <Input data-testid="svc-new-key" placeholder="service_key" value={newSvc.key} onChange={(e) => setNewSvc({ ...newSvc, key: e.target.value })} className="flex-1" />
              <Input data-testid="svc-new-points" type="number" value={newSvc.points} onChange={(e) => setNewSvc({ ...newSvc, points: e.target.value })} className="w-24" />
              <Button data-testid="svc-add-btn" variant="outline" size="icon" onClick={addService}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="rules-save-btn" onClick={save} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Save className="h-4 w-4 mr-2" /> Save all rules</Button>
      </div>
    </AdminShell>
  );
}
